import { config, GENDER } from '../../pedigree/config';
import {
  relationship,
  getRelationshipForNewPerson
} from '../../pedigree/utils';
import {
  adoptableChildrenForNewSpouse,
  newSpouseSlotsFor,
  addSiblingsToRecords,
  addChildrenToRecords
} from '../../pedigree/edgeIntegrity';
import { mintObjectIdHex } from '../../pedigree/saveReconciliation';
import {
  GENDER_IDENTITY,
  spouseRelations
} from 'src/components/questionnaire/Constants';

const getNextId = (dataset) => {
  if (!dataset || dataset.length === 0) return 1;

  const numericIds = dataset.map((p) => {
    if (typeof p.pid === 'number') return p.pid;
    else return 0;
  });

  return Math.max(...numericIds) + 1;
};

export const createPerson = (
  gender,
  user_id,
  dataset = [],
  relationshipValue = ''
) => {
  const pid = getNextId(dataset);
  return {
    user_id,
    pid,
    uid: mintObjectIdHex(),
    gender,
    genderIdentity: GENDER_IDENTITY[gender] ?? GENDER_IDENTITY.unknown,
    motherId: null,
    fatherId: null,
    spouseIds: [],
    childrenIds: [],
    siblingIds: [],
    relationship: relationshipValue,
    isDynamicallyAdded: true
  };
};

const ownerPidOf = (node) =>
  node.isProxy && node.proxyOf != null ? node.proxyOf : node.pid;

const isNode = (node) => node?.kind === config.NODE_TYPE.NODE;

// Every action takes an optional trailing `opts` carrying `onCreate` — called
// once per person the action mints, in creation order, with the person object
// itself. The sidebar panel uses it to build AddBatch.createdIds so an add can
// be undone; the keyboard layer passes nothing and is unaffected.
//
// It has to be a callback rather than a second return value because these run
// INSIDE a setMemberRecords updater (that is the rapid-add fix — everything
// resolves against the latest array), and an updater can only return the next
// state. The objects handed to onCreate are the same ones that end up in the
// array, so reading `.pid` off them after the updater has run is safe. Callers
// must therefore reset their collector before each add rather than assuming a
// single invocation — StrictMode is off today (src/index.js:31) but this does
// not depend on that.
const noteCreated = (opts, person) => {
  opts?.onCreate?.(person);
  return person;
};

export const addParents = (members, node, user, opts) => {
  if (!isNode(node)) return members;
  const ownerPid = ownerPidOf(node);

  // Idempotent, matching the in-component version this replaced: a re-fire
  // (double-click, or a keyboard repeat) must not create a second couple and
  // orphan the first by overwriting fatherId/motherId. Resolved from `members`
  // rather than `node` because the updater sees the latest array while the
  // node datum can be a render behind.
  const existing = members.find((p) => p.pid === ownerPid);
  if (!existing || existing.fatherId || existing.motherId) return members;

  const fatherRel = getRelationshipForNewPerson(
    node,
    user,
    relationship.father
  );
  const motherRel = getRelationshipForNewPerson(
    node,
    user,
    relationship.mother
  );

  const prev = members;
  const father = noteCreated(
    opts,
    createPerson('male', user._id, prev, fatherRel)
  );
  const mother = noteCreated(
    opts,
    createPerson('female', user._id, [...prev, father], motherRel)
  );

  father.spouseIds.push(mother.pid);
  mother.spouseIds.push(father.pid);
  father.spouseRelationships = {
    [mother.pid]: {
      parentRelationship: spouseRelations.MARRIED,
      isConsanguineous: false
    }
  };
  mother.spouseRelationships = {
    [father.pid]: {
      parentRelationship: spouseRelations.MARRIED,
      isConsanguineous: false
    }
  };
  father.childrenIds.push(ownerPid);
  mother.childrenIds.push(ownerPid);

  return prev
    .map((person) =>
      person.pid === ownerPid
        ? { ...person, fatherId: father.pid, motherId: mother.pid }
        : person
    )
    .concat([father, mother]);
};

export const addSpouse = (members, node, user, opts) => {
  if (!isNode(node)) return members;
  const ownerPid = ownerPidOf(node);

  const spouseRel = getRelationshipForNewPerson(
    node,
    user,
    relationship.spouse
  );

  const prev = members;
  const self = prev.find((person) => person.pid === ownerPid);
  if (!self) return prev;

  // The spouse's slot and placeholder sex follow the node's REPRODUCTIVE sex and,
  // for an unknown-sex node, the slot its children already give it — one answer
  // shared with adoptableChildrenForNewSpouse (TGCLOUD-1786), never a raw
  // `gender === 'male'` test.
  const { spouseGender, spouseSlotKey } = newSpouseSlotsFor(prev, self);
  const spouse = createPerson(spouseGender, user._id, prev, spouseRel);

  const adoptable = new Set(adoptableChildrenForNewSpouse(prev, self));

  const arr = prev.map((person) => {
    if (person.pid === ownerPid) {
      return {
        ...person,
        spouseIds: [...person.spouseIds, spouse.pid],
        spouseRelationships: {
          ...(person.spouseRelationships || {}),
          [spouse.pid]: {
            parentRelationship: spouseRelations.MARRIED,
            isConsanguineous: false
          }
        }
      };
    }

    if (adoptable.has(person.pid)) {
      return { ...person, [spouseSlotKey]: spouse.pid };
    }

    return person;
  });

  const newSpouse = {
    ...spouse,
    spouseIds: [ownerPid],
    spouseRelationships: {
      [ownerPid]: {
        parentRelationship: spouseRelations.MARRIED,
        isConsanguineous: false
      }
    },
    childrenIds: [...adoptable]
  };

  const nodeIndex = arr.findIndex((member) => member.pid === ownerPid);

  let insertIndex = nodeIndex + 1;
  (self.spouseIds || []).forEach((existingSpouseId) => {
    const spouseIdx = arr.findIndex((m) => m.pid === existingSpouseId);
    if (spouseIdx !== -1 && spouseIdx + 1 > insertIndex) {
      insertIndex = spouseIdx + 1;
    }
  });
  arr.splice(insertIndex, 0, newSpouse);
  noteCreated(opts, newSpouse);
  return arr;
};

export const addGrandparents = (members, node, user, opts) => {
  if (!isNode(node)) return members;
  const ownerPid = ownerPidOf(node);

  const prev = members;
  const self = prev.find((p) => p.pid === ownerPid) || node;
  const selfHasBothParents = !!(self.fatherId && self.motherId);

  // Idempotent, matching the in-component version this replaced: if either
  // existing parent already has parents (the same predicate that turns the tile
  // into a "Grandparents recorded" chip), a re-fire must not create a second set
  // and re-point the parents at them.
  if (selfHasBothParents) {
    const fatherRec = prev.find((p) => p.pid === self.fatherId);
    const motherRec = prev.find((p) => p.pid === self.motherId);
    if (
      fatherRec?.fatherId ||
      fatherRec?.motherId ||
      motherRec?.fatherId ||
      motherRec?.motherId
    ) {
      return prev;
    }
  }

  const newPeople = [];
  let updatedData = [...prev];
  let fatherId = self.fatherId ?? null;
  let motherId = self.motherId ?? null;

  //Create parents if they don't exist
  if (!selfHasBothParents) {
    if (!fatherId) {
      const father = noteCreated(
        opts,
        createPerson(
          'male',
          user._id,
          [...updatedData, ...newPeople],
          getRelationshipForNewPerson(node, user, relationship.father)
        )
      );
      fatherId = father.pid;
      newPeople.push(father);
    }

    if (!motherId) {
      const mother = noteCreated(
        opts,
        createPerson(
          'female',
          user._id,
          [...updatedData, ...newPeople],
          getRelationshipForNewPerson(node, user, relationship.mother)
        )
      );
      motherId = mother.pid;
      newPeople.push(mother);
    }

    if (!self.fatherId && !self.motherId) {
      const father = newPeople.find((p) => p.pid === fatherId);
      const mother = newPeople.find((p) => p.pid === motherId);
      if (father && mother) {
        father.spouseIds.push(motherId);
        mother.spouseIds.push(fatherId);
        father.spouseRelationships = {
          [motherId]: {
            parentRelationship: spouseRelations.MARRIED,
            isConsanguineous: false
          }
        };
        mother.spouseRelationships = {
          [fatherId]: {
            parentRelationship: spouseRelations.MARRIED,
            isConsanguineous: false
          }
        };
      }
    }

    // Link parents to child
    const father =
      newPeople.find((p) => p.pid === fatherId) ||
      updatedData.find((p) => p.pid === fatherId);
    const mother =
      newPeople.find((p) => p.pid === motherId) ||
      updatedData.find((p) => p.pid === motherId);

    if (father && !father.childrenIds.includes(ownerPid)) {
      father.childrenIds.push(ownerPid);
    }
    if (mother && !mother.childrenIds.includes(ownerPid)) {
      mother.childrenIds.push(ownerPid);
    }

    updatedData = updatedData.map((person) => {
      if (person.pid === ownerPid) {
        return {
          ...person,
          fatherId: fatherId,
          motherId: motherId
        };
      }
      return person;
    });
  }

  // Add  grandparents : father-side
  const paternalGrandfather = noteCreated(
    opts,
    createPerson(
      'male',
      user._id,
      [...updatedData, ...newPeople],
      relationship.fathersFather
    )
  );
  newPeople.push(paternalGrandfather);

  const paternalGrandmother = noteCreated(
    opts,
    createPerson(
      'female',
      user._id,
      [...updatedData, ...newPeople],
      relationship.fathersMother
    )
  );
  newPeople.push(paternalGrandmother);

  paternalGrandfather.spouseIds.push(paternalGrandmother.pid);
  paternalGrandmother.spouseIds.push(paternalGrandfather.pid);
  paternalGrandfather.spouseRelationships = {
    [paternalGrandmother.pid]: {
      parentRelationship: spouseRelations.MARRIED,
      isConsanguineous: false
    }
  };
  paternalGrandmother.spouseRelationships = {
    [paternalGrandfather.pid]: {
      parentRelationship: spouseRelations.MARRIED,
      isConsanguineous: false
    }
  };

  paternalGrandfather.childrenIds.push(fatherId);
  paternalGrandmother.childrenIds.push(fatherId);

  // Add  grandparents: mother-side
  const maternalGrandfather = noteCreated(
    opts,
    createPerson(
      'male',
      user._id,
      [...updatedData, ...newPeople],
      relationship.mothersFather
    )
  );
  newPeople.push(maternalGrandfather);

  const maternalGrandmother = noteCreated(
    opts,
    createPerson(
      'female',
      user._id,
      [...updatedData, ...newPeople],
      relationship.mothersMother
    )
  );
  newPeople.push(maternalGrandmother);

  maternalGrandfather.spouseIds.push(maternalGrandmother.pid);
  maternalGrandmother.spouseIds.push(maternalGrandfather.pid);
  maternalGrandfather.spouseRelationships = {
    [maternalGrandmother.pid]: {
      parentRelationship: spouseRelations.MARRIED,
      isConsanguineous: false
    }
  };
  maternalGrandmother.spouseRelationships = {
    [maternalGrandfather.pid]: {
      parentRelationship: spouseRelations.MARRIED,
      isConsanguineous: false
    }
  };

  maternalGrandfather.childrenIds.push(motherId);
  maternalGrandmother.childrenIds.push(motherId);

  newPeople.forEach((person) => {
    if (person.pid === fatherId) {
      person.fatherId = paternalGrandfather.pid;
      person.motherId = paternalGrandmother.pid;
    }
    if (person.pid === motherId) {
      person.fatherId = maternalGrandfather.pid;
      person.motherId = maternalGrandmother.pid;
    }
  });

  updatedData = updatedData.map((person) => {
    if (person.pid === fatherId) {
      return {
        ...person,
        fatherId: paternalGrandfather.pid,
        motherId: paternalGrandmother.pid
      };
    }
    if (person.pid === motherId) {
      return {
        ...person,
        fatherId: maternalGrandfather.pid,
        motherId: maternalGrandmother.pid
      };
    }
    return person;
  });

  return [...updatedData, ...newPeople];
};

// `opts.coParentPid` scopes the add to ONE union on a multi-spouse person. It
// used to be dropped here, so a keyboard child-add always landed on the most
// recently added spouse regardless of what the sidebar had selected — the
// divergence the panel rewrite forced into the open. addChildrenToRecords falls
// back to the last spouse when it is null or not actually a spouse, which is the
// historical behaviour, so passing nothing is unchanged.
//
// A child with NO spouse recorded is legitimate and stays supported: the
// transform resolves a single-parent child rather than refusing.
export const addChildren = (
  members,
  node,
  user,
  gender,
  count = 1,
  opts = {}
) => {
  if (!isNode(node)) return members;
  if (count < 1) return members;
  const ownerPid = ownerPidOf(node);
  const relationshipValue = getRelationshipForNewPerson(
    node,
    user,
    gender === 'male' ? relationship.son : relationship.daughter
  );
  return addChildrenToRecords(members, ownerPid, {
    count,
    coParentPid: opts.coParentPid ?? null,
    makeChild: () =>
      noteCreated(opts, createPerson(gender, user._id, [], relationshipValue))
  });
};

export const addSiblings = (
  members,
  node,
  user,
  gender,
  count = 1,
  opts = {}
) => {
  if (!isNode(node)) return members;
  // A sibling needs a parent to hang off — the sibship is derived from the
  // shared parents, not from a sibling-of pointer. Resolved from `members` so a
  // "Parents then Brother" burst reads the parents the first add just created.
  const self = members.find((p) => p.pid === ownerPidOf(node));
  const hasParents = (self ?? node).fatherId || (self ?? node).motherId;
  if (!hasParents || count < 1) return members;
  const ownerPid = ownerPidOf(node);
  const relationshipValue = getRelationshipForNewPerson(
    node,
    user,
    gender === 'male' ? relationship.fullBrother : relationship.fullSister
  );
  return addSiblingsToRecords(members, ownerPid, {
    count,
    makeSibling: () =>
      noteCreated(opts, createPerson(gender, user._id, [], relationshipValue))
  });
};

// Single sibling with twin support (context-menu SiblingMenu). Mirrors
// addSibling: only the kind guard — no parent requirement.
// `siblingType` is still accepted in the payload because the menu passes it, but it is
// NOT forwarded: `twinType` alone decides co-multiple status, and the sibling type is
// derived from the size of the resulting set (TGCLOUD-1692).
export const addSibling = (
  members,
  node,
  user,
  { gender, twinType },
  opts = {}
) => {
  if (!isNode(node)) return members;
  const ownerPid = ownerPidOf(node);
  const relationshipValue = getRelationshipForNewPerson(
    node,
    user,
    gender === 'male' ? relationship.fullBrother : relationship.fullSister
  );
  return addSiblingsToRecords(members, ownerPid, {
    count: 1,
    twinType,
    makeSibling: () =>
      noteCreated(opts, createPerson(gender, user._id, [], relationshipValue))
  });
};

// The unconnected person: unknown sex AND unknown relationship, no edges at all.
// NOT a sex-unknown child or sibling — `relationship.otherAffected` means an
// other/unrelated person, which is why this never carried a count.
export const addUnknown = (members, node, user, opts = {}) => {
  return [
    ...members,
    noteCreated(
      opts,
      createPerson('unknown', user._id, members, relationship.otherAffected)
    )
  ];
};

// Convenience gender-typed aliases used by the keyboard layer and the sidebar
// panel. `opts` carries coParentPid / onCreate; the keyboard layer omits it.
export const addSon = (members, node, user, count = 1, opts = {}) =>
  addChildren(members, node, user, GENDER.male, count, opts);
export const addDaughter = (members, node, user, count = 1, opts = {}) =>
  addChildren(members, node, user, GENDER.female, count, opts);
export const addBrother = (members, node, user, count = 1, opts = {}) =>
  addSiblings(members, node, user, GENDER.male, count, opts);
export const addSister = (members, node, user, count = 1, opts = {}) =>
  addSiblings(members, node, user, GENDER.female, count, opts);
